import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  isEmpty,
  isNil,
} from 'ramda';
import { withStyles } from '@material-ui/core/styles';
import LinearProgress from '@material-ui/core/LinearProgress';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import { withApollo } from 'react-apollo';
import {
  stylesToPropType,
  handleChange,
  isUrl,
} from '../../utils';
import { GET_PING_ID, UPDATE_PING_ID } from './queries';

const styles = () => ({
  root: {
    width: '100%',
  },
  disabled: {
    '& *': {
      color: '#ccc',
      cursor: 'default',
    },
    '& input': {
      pointerEvents: 'none',
    },
  },
  saveButton: {
    borderRadius: '0.125em',
    marginTop: '1em',
    width: '5em',
  },
});

class PingID extends Component {
  constructor(props) {
    super(props);

    this.state = {
      pingID: {},
      pendingChanges: false,
      error: null,
      authStringError: null,
      serverUrlError: null,
      loading: true,
    };
  }

  async componentDidMount() {
    const { appKey, client } = this.props;

    this.setState({ loading: true });

    try {
      const { data: { pingID } } = await client.query({
        query: GET_PING_ID,
        variables: { appKey },
      });
      this.setState({
        pingID: {
          app_key: appKey,
          auth_string: pingID.auth_string,
          server_url: pingID.server_url,
        },
        loading: false,
      });
    } catch (error) {
      this.setState({ error });
    } finally {
      this.setState({ loading: false });
    }
  }

  saveChanges = async () => {
    const { client } = this.props;
    const {
      pingID,
      pendingChanges,
    } = this.state;

    const isAuthStringInvalid = isNil(pingID.auth_string) || isEmpty(pingID.auth_string);
    const isServerUrlInvalid = (
      isNil(pingID.server_url)
      || isEmpty(pingID.server_url)
      || !isUrl(pingID.server_url)
    );
    const hasError = isAuthStringInvalid || isServerUrlInvalid;

    if (hasError) {
      const update = {
        authStringError: isAuthStringInvalid ? new Error('Server Authentication must be non-empty') : null,
        serverUrlError: isServerUrlInvalid ? new Error('Server URL must be a valid URL') : null,
        error: new Error('Please fix all errors before saving'),
      };
      this.setState(update);
    } else if (
      pendingChanges
    ) {
      this.setState({ loading: true });

      try {
        await client.mutate({
          mutation: UPDATE_PING_ID,
          variables: { pingID },
        });
        this.setState({ loading: false, pendingChanges: false, error: null });
      } catch (error) {
        this.setState({ error });
      } finally {
        this.setState({ loading: false });
      }
    }
  }

  render() {
    const { classes } = this.props;
    const {
      pingID: {
        auth_string: authString,
        server_url: serverUrl,
      },
      pendingChanges,
      error,
      authStringError,
      serverUrlError,
      loading,
    } = this.state;

    const saveButtonClass = `${
      classes.saveButton
    } ${
      pendingChanges || classes.disabled
    }`;

    return (
      <Fragment>
        <Typography
          component="div"
          variant="subheading"
          gutterBottom
        >
          PingID Settings
        </Typography>
        {loading && <LinearProgress />}
        <form>
          <FormControl className={classes.root} error={!!authStringError}>
            <TextField
              label="Server Authentication"
              value={authString || ''}
              onChange={(event) => {
                handleChange(this, ['pingID', 'auth_string'], event);
                this.setState({
                  authStringError: null,
                  pendingChanges: true,
                });
              }}
              type="text"
              InputLabelProps={{ shrink: true }}
              margin="normal"
            />
            {authStringError && <FormHelperText>{authStringError.message}</FormHelperText>}

            <Button
              variant="contained"
              className={saveButtonClass}
              onClick={this.saveChanges}
            >
              Save
            </Button>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        </form>
      </Fragment>
    );
  }
}

PingID.propTypes = {
  appKey: PropTypes.string.isRequired,
  classes: stylesToPropType(styles()).isRequired,
  client: PropTypes.object.isRequired,
};

export default withStyles(styles)(withApollo(PingID));
